<template>
  <div>
    <div id="portal-links" class="widget portal-links">
      <h2 class="title">Portal links</h2>
      <span id="portal-links-loading-spinner" v-if="containerLoading" class="container-spinner"></span>
      <div id="portal-links-container" v-else>
        <div v-for="portalLink in portalLinks" :key="portalLink.url" class="portallink-card">
          <div class="portallink-card-content">
            <img src="@/assets/icon_link.svg" class="portallink-icon">
            <div class="portallink-card-content-inner">
              <h4 class="portallink-title">
                <a class="portallink-url" :href="portalLink.url" :portallinkid="portalLink.portallinkid" :portal-userfavourite-id="portalLink.userfavouriteid" target="_blank">{{ portalLink.label }}</a>
              </h4>
              <img src="@/assets/star_fill.png" v-if="portalLink.isFavourite" class="portallink-star" @click="removeFavourite(portalLink)">
              <img src="@/assets/star_non_fill.png" v-else class="portallink-star" @click="addFavourite(portalLink)">
              <span v-if="portalLink.loading" class="spinner"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Debug -->
    <!-- <button id="hideportallinks">Toggle debug info</button>
    <div id="debuginfo1">
      <p>API Response below</p>
      <pre>{{ apiResponse }}</pre>
    </div> -->
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { userGuid, postDataverse, deleteDataverse, queryDataverseFetchXml } from '@/functions/Dataverse';
import eventBus from '@/functions/eventBus';

// Local state variables to store API response and portal links
const apiResponse = ref('');
const portalLinks = ref([]);
const containerLoading = ref(false); // New state for container loading
let firstTimeLoading = true;

// Function to fetch user favourites
const fetchUserFavouritesIds = async () => {
  try {
    const tableName = 'sdportal_userfavourites';
    const result = await queryDataverseFetchXml(tableName);
    console.log("fetchUserFavouritesIds: ", result);
    return result.value;
  } catch {
    console.log('Not fetching user favourites:');
    apiResponse.value = 'An error occurred while fetching user favourites.';
    return [];
  }
};

// Function to fetch portal links and update the state
const fetchPortalLinks = async () => {
  try {
    if (firstTimeLoading) {
      containerLoading.value = true; // Set container loading to true
      firstTimeLoading = false;
    }
    const tableName = 'sdportal_portallinks';
    const result = await queryDataverseFetchXml(tableName);
    
    const userFavouriteIds = await fetchUserFavouritesIds();
    const userFavouriteIdsArray = userFavouriteIds.map(fav => fav['sdportal_portallink2.sdportal_portallinkid']);

    console.log("userFavouriteIds: ", userFavouriteIdsArray);

    // Update local state variable with API response
    apiResponse.value = result;
    console.log(apiResponse.value);

    portalLinks.value = apiResponse.value.value.map(item => {
      const attributes = item;
      const sequence = attributes.sdportal_sequence;
      const url = attributes.sdportal_url;
      const label = attributes.sdportal_label;
      const portallinkid = attributes.sdportal_portallinkid;
      // userfavouriteid may be null
      const userfavouriteid = attributes['sdportal_userfavourite1.sdportal_userfavouriteid'];

      const isFavourite = userFavouriteIdsArray.includes(portallinkid);
      return { sequence, url, label, portallinkid, userfavouriteid, isFavourite, loading: false };
    }).sort((a, b) => a.sequence - b.sequence).slice(0, 5);
  } catch {
    console.log('Not fetching portal links:');
    apiResponse.value = 'An error occurred while fetching Portal Links.';
  } finally {
    containerLoading.value = false; // Set container loading to false
  }
};

const addFavourite = async (portalLink) => {
  console.log('addFavourite');
  portalLink.loading = true; // Set loading to true for the specific portalLink
  const tableName = 'sdportal_userfavourites';
  const userFavouriteIds = await fetchUserFavouritesIds();
  const maxSequence = Math.max(...userFavouriteIds.map(fav => fav.sdportal_sequence), 0);
  const newSequence = maxSequence + 1;
  const userGuidValue = await userGuid(); // Corrected line to use userGuid
  
  const data = {
    "sdportal_sequence": newSequence,
    "sdportal_PortalLink@odata.bind": `/sdportal_portallinks(${portalLink.portallinkid})`,
    "sdportal_User@odata.bind": `/contacts(${userGuidValue})`
  };
  console.log('addFavourite data:', data);

  await postDataverse(tableName, data);
  await fetchPortalLinks(); // Refresh the portal links to reflect changes
  portalLink.loading = false; // Set loading to false for the specific portalLink

  eventBus.value.dispatchEvent(new CustomEvent('favouriteAdded', { detail: portalLink }));
};

const removeFavourite = async (portalLink) => {
  console.log('removeFavourite');
  portalLink.loading = true; // Set loading to true for the specific portalLink
  const tableName = 'sdportal_userfavourites';
  await deleteDataverse(tableName, portalLink.userfavouriteid);
  await fetchPortalLinks(); // Refresh the portal links to reflect changes
  portalLink.loading = false; // Set loading to false for the specific portalLink

  eventBus.value.dispatchEvent(new CustomEvent('favouriteRemoved', { detail: portalLink }));
};

// Call fetchDataProtectionOfficeArticles when the component is mounted
onMounted(() => {
    fetchPortalLinks();

  // const hideButton = document.getElementById('hideportallinks');
  // hideButton.addEventListener('click', function() {
  //   const debuginfo1 = document.getElementById('debuginfo1');
  //   if (debuginfo1.style.display === 'none') {
  //     debuginfo1.style.display = 'block';
  //   } else {
  //     debuginfo1.style.display = 'none';
  //   }
  // });
  // hideButton.style.display = 'block';
});
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 10px;
  height: 10px;
  animation: spin 1s linear infinite;
}

.container-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
  display: block;
  margin: 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
/* hide the hide button */
#hideportallinks {
  background-color: white;
}

#debuginfo1 {
  display: none;
}
.title {
  font-weight: 600;
}
.portallink-star {
  cursor: pointer; 
  padding-right: 10px;
}
html,
body,
p,
.p,
.page_section p,
.section-diagonal-left p,
.section-diagonal-right p,
footer .footer-bottom p,
select, option, label, span, button {
  font-size: 14px;
  font-family: 'Source Sans 3', 'Microsoft YaHei', 'Microsoft JhengHei', "Glyphicons Halflings", sans-serif !important;
  font-weight: 400;
}

h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6 {
  font-family: 'Source Sans 3', 'Microsoft YaHei', 'Microsoft JhengHei', sans-serif !important;
  font-weight: 400;
}

a {
  text-decoration: none;
  color: black;
}

.portallink-card {
  width: 35%;
}

.portallink-card-content {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.portallink-card-content-inner {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.portallink-title {
  padding: 0 10px;
}


#portal-links {
  padding: 1rem;
}

.myfavorite-card-content {
  background-color: #f3f8f5;
  padding: 15px;
}

.myFavourite-title {
  text-decoration: none;
  color: black;
  padding: 0;
  margin: 0;
}

.title {
  font-weight: 600;
}

.favorite-row {
  display: flex;
}

.myFavourite-card {
  max-width: 100%;
  width: 15%;
  padding: 0 10px;
}
</style>